import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsUpDown, Loader } from "lucide-react";
import { toast } from "sonner";

import {
  getListLinksQueryKey, getListNodesQueryKey, useCreateLink, useCreateNode, useUpdateLink, useUpdateNode,
  type Link, type LinkInput, type LinkType, type Node, type NodeInput, type NodeType,
} from "@/api/generated";
import { Combobox, type Option } from "@/components/common/Combobox";
import { ErrorNote, Field } from "@/components/common/common";
import { INTERNAL, TenantSelect, tenantId, tenantValue } from "@/components/common/TenantSelect";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { nodePath, useLinkMap, useNodeMap } from "@/lib/lookups";
import { cn } from "@/lib/utils";

const NONE = "__none__";

export const NODE_TYPES: NodeType[] = ["REGION", "CITY", "POP", "DATACENTER", "HALL", "MMR", "RACK", "DEVICE"];
export const LINK_TYPES: LinkType[] = ["SUBSEA", "TERRESTRIAL", "METRO_FIBER", "CROSS_CONNECT", "TRANSIT", "PEERING"];

export const typeLabel = (t: string) => t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, " ");

const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s));

/** Links for a Combobox, by name, each with its ends as the hint. */
export function useLinkOptions(exclude?: string): Option[] {
  const links = useLinkMap();
  const nodes = useNodeMap();
  return useMemo(
    () =>
      [...links.values()]
        .filter((l) => l.id !== exclude)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((l) => ({ value: l.id, label: l.name, hint: `${nodes.get(l.a_node_id)?.name ?? "?"} – ${nodes.get(l.z_node_id)?.name ?? "?"}` })),
    [links, nodes, exclude],
  );
}

const pathLabel = (nodes: Map<string, Node>, id: string) => nodePath(nodes, id).map((x) => x.name).join(" › ");

/**
 * Searchable location picker: the explorer's tree, indented; a search lists
 * the matches (name or slug, as the explorer's filter) with their parents, so
 * Enter takes the first match. The trigger leads with the node's name, so a
 * long path truncates without hiding which node is chosen. `none` adds a first
 * choice that is not a node ("Top level").
 */
export function NodePicker({
  value, onChange, exclude, none, placeholder = "Choose a location", "aria-describedby": describedBy,
}: {
  value: string; onChange: (v: string) => void; exclude?: Set<string>;
  none?: { value: string; label: string }; placeholder?: string; "aria-describedby"?: string;
}) {
  const nodes = useNodeMap();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const kids = new Map<string, Node[]>();
    const roots: Node[] = [];
    for (const n of nodes.values()) {
      if (exclude?.has(n.id)) continue;
      if (n.parent_id && nodes.has(n.parent_id)) kids.set(n.parent_id, [...(kids.get(n.parent_id) ?? []), n]);
      else roots.push(n);
    }
    const out: { node: Node; depth: number; parents?: string }[] = [];
    const walk = (list: Node[], depth: number, parents: string[]) => {
      for (const n of [...list].sort((a, b) => a.name.localeCompare(b.name))) {
        if (!needle) out.push({ node: n, depth });
        else if (n.name.toLowerCase().includes(needle) || n.slug.includes(needle)) out.push({ node: n, depth: 0, parents: parents.join(" › ") });
        walk(kids.get(n.id) ?? [], depth + 1, [...parents, n.name]);
      }
    };
    walk(roots, 0, []);
    return out;
  }, [nodes, exclude, needle]);

  const current = nodes.get(value);
  const parents = current ? nodePath(nodes, current.id).slice(0, -1).map((x) => x.name).join(" › ") : "";
  const isNone = !current && none?.value === value;
  const choose = (v: string) => {
    onChange(v);
    // Closing through state skips onOpenChange: clear the search here too.
    setOpen(false);
    setSearch("");
  };
  // cmdk only scrolls on keyboard moves: bring the current choice into view on open.
  const reveal = useCallback((list: HTMLDivElement | null) => {
    const item = list?.querySelector<HTMLElement>("[data-checked=true]");
    if (list && item) list.scrollTop += item.getBoundingClientRect().top - list.getBoundingClientRect().top - list.clientHeight / 2;
  }, []);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" role="combobox" aria-expanded={open} aria-describedby={describedBy}
          title={current ? pathLabel(nodes, current.id) : undefined}
          className="w-full justify-between font-normal"
        >
          {current ? (
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="max-w-full shrink-0 truncate">{current.name}</span>
              {parents && <span className="truncate text-xs text-muted-foreground">{parents}</span>}
            </span>
          ) : (
            <span className={cn("truncate", !isNone && "text-muted-foreground")}>{isNone ? none?.label : placeholder}</span>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* At least 20rem wide, but never wider than the space there is: min-width beats max-width in CSS. */}
      <PopoverContent className="w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width) min-w-[min(20rem,var(--radix-popover-content-available-width))] p-0" align="start">
        <Command shouldFilter={false} defaultValue={value}>
          <CommandInput value={search} onValueChange={setSearch} placeholder="Find a location" />
          <CommandList ref={reveal}>
            <CommandEmpty>No location matches.</CommandEmpty>
            <CommandGroup>
              {none && !needle && (
                <CommandItem value={none.value} data-checked={isNone} onSelect={() => choose(none.value)}>{none.label}</CommandItem>
              )}
              {rows.map(({ node: n, depth, parents }) => (
                <CommandItem
                  key={n.id} value={n.id} data-checked={n.id === value} onSelect={() => choose(n.id)}
                  aria-label={pathLabel(nodes, n.id)} style={{ paddingLeft: 8 + depth * 12 }}
                >
                  <span className="flex min-w-0 flex-1 items-baseline gap-2">
                    <span className="max-w-full shrink-0 truncate">{n.name}</span>
                    {parents && <span className="truncate text-xs text-muted-foreground">{parents}</span>}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">{typeLabel(n.type)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Children of id, recursively (used to keep a node from moving under itself). */
export function subtree(nodes: Map<string, Node>, id: string): Set<string> {
  const kids = new Map<string, string[]>();
  for (const n of nodes.values()) if (n.parent_id) kids.set(n.parent_id, [...(kids.get(n.parent_id) ?? []), n.id]);
  const out = new Set<string>([id]);
  const stack = [id];
  while (stack.length) for (const c of kids.get(stack.pop()!) ?? []) if (!out.has(c)) { out.add(c); stack.push(c); }
  return out;
}

export function NodeDialog({
  open, onOpenChange, node, parentId, onSaved,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; node?: Node; parentId?: string | null; onSaved?: (n: Node) => void;
}) {
  const qc = useQueryClient();
  const nodes = useNodeMap();
  const create = useCreateNode();
  const update = useUpdateNode();
  const m = node ? update : create;
  const exclude = useMemo(() => (node ? subtree(nodes, node.id) : undefined), [nodes, node]);
  const [f, setF] = useState({ name: "", slug: "", type: "CITY" as NodeType, parent: NONE, lat: "", lon: "", ref: "", tenant: INTERNAL });
  useEffect(() => {
    if (!open) return;
    const parent = node ? node.parent_id : parentId;
    const parentType = parent ? nodes.get(parent)?.type : undefined;
    const nextType = parentType ? NODE_TYPES[Math.min(NODE_TYPES.indexOf(parentType) + 1, NODE_TYPES.length - 1)] : "REGION";
    setF({
      name: node?.name ?? "", slug: node?.slug ?? "", type: node?.type ?? nextType, parent: parent ?? NONE,
      lat: node?.latitude?.toString() ?? "", lon: node?.longitude?.toString() ?? "", ref: node?.external_ref ?? "",
      tenant: tenantValue(node?.tenant_id),
    });
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, node?.id, parentId]);
  const set = (k: keyof typeof f, v: string) => setF((c) => ({ ...c, [k]: v }));
  const save = () => {
    const data: NodeInput = {
      name: f.name.trim(), type: f.type, parent_id: f.parent === NONE ? null : f.parent,
      latitude: numOrNull(f.lat), longitude: numOrNull(f.lon), external_ref: f.ref.trim() || null,
      // Replace semantics: keep what this form does not edit.
      layout: node?.layout ?? null, tenant_id: tenantId(f.tenant),
      ...(f.slug.trim() ? { slug: f.slug.trim() } : {}),
    };
    const done = {
      onSuccess: (n: Node) => {
        toast.success(node ? "Node saved" : "Node created", { description: n.name });
        qc.invalidateQueries({ queryKey: getListNodesQueryKey() });
        onOpenChange(false);
        onSaved?.(n);
      },
    };
    if (node) update.mutate({ id: node.id, data }, done);
    else create.mutate({ data }, done);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader className="pr-6">
          <DialogTitle className="leading-snug wrap-anywhere">{node ? `Edit ${node.name}` : "New node"}</DialogTitle>
          <DialogDescription>Region › city › PoP or datacenter › hall or MMR › rack › device.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
            <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus /></Field>
            <Field label="Type">
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{NODE_TYPES.map((t) => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Inside">
            <NodePicker value={f.parent} onChange={(v) => set("parent", v)} exclude={exclude} none={{ value: NONE, label: "Top level" }} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Latitude"><Input value={f.lat} onChange={(e) => set("lat", e.target.value)} inputMode="decimal" placeholder="-6.2" /></Field>
            <Field label="Longitude"><Input value={f.lon} onChange={(e) => set("lon", e.target.value)} inputMode="decimal" placeholder="106.8" /></Field>
            <Field label="Slug" hint="Used in bookmarkable URLs. Empty: made from the name.">
              <Input value={f.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="cgk-dc3" />
            </Field>
            <Field label="External reference" hint="ID in your inventory system, for imports.">
              <Input value={f.ref} onChange={(e) => set("ref", e.target.value)} />
            </Field>
          </div>
          <TenantSelect value={f.tenant} onChange={(v) => set("tenant", v)} label="Customer site of"
            hint="A customer's own site shows by name on their network map; anything else is anonymous to them." />
        </div>
        <ErrorNote error={m.error} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!f.name.trim() || m.isPending}>{m.isPending && <Loader className="size-3.5 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LinkDialog({
  open, onOpenChange, link, nodeId,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; link?: Link; nodeId?: string;
}) {
  const qc = useQueryClient();
  const create = useCreateLink();
  const update = useUpdateLink();
  const m = link ? update : create;
  const [f, setF] = useState({ name: "", a: "", z: "", type: "TERRESTRIAL" as LinkType, provider: "", circuit: "", capacity: "", rtt: "", depends: NONE, ref: "", tenant: INTERNAL });
  useEffect(() => {
    if (!open) return;
    setF({
      name: link?.name ?? "", a: link?.a_node_id ?? nodeId ?? "", z: link?.z_node_id ?? "", type: link?.link_type ?? "TERRESTRIAL",
      provider: link?.provider ?? "", circuit: link?.circuit_id ?? "", capacity: link?.capacity_mbps?.toString() ?? "",
      rtt: link?.expected_rtt_ms?.toString() ?? "", depends: link?.depends_on_link_id ?? NONE, ref: link?.external_ref ?? "",
      tenant: tenantValue(link?.tenant_id),
    });
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, link?.id, nodeId]);
  const set = (k: keyof typeof f, v: string) => setF((c) => ({ ...c, [k]: v }));
  const save = () => {
    const data: LinkInput = {
      name: f.name.trim(), a_node_id: f.a, z_node_id: f.z, link_type: f.type,
      provider: f.provider.trim() || null, circuit_id: f.circuit.trim() || null,
      capacity_mbps: numOrNull(f.capacity), expected_rtt_ms: numOrNull(f.rtt),
      depends_on_link_id: f.depends === NONE ? null : f.depends, external_ref: f.ref.trim() || null,
      tenant_id: tenantId(f.tenant),
    };
    const done = {
      onSuccess: (l: Link) => {
        toast.success(link ? "Link saved" : "Link created", { description: l.name });
        qc.invalidateQueries({ queryKey: getListLinksQueryKey() });
        onOpenChange(false);
      },
    };
    if (link) update.mutate({ id: link.id, data }, done);
    else create.mutate({ data }, done);
  };
  const dependsOptions = [{ value: NONE, label: "Nothing" }, ...useLinkOptions(link?.id)];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader className="pr-6">
          <DialogTitle className="leading-snug wrap-anywhere">{link ? `Edit ${link.name}` : "New link"}</DialogTitle>
          <DialogDescription>A path between two nodes: a subsea cable, a metro fiber, a cross-connect, a transit or peering session.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="CGK–SEA subsea" autoFocus /></Field>
          <Field label="Type">
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{LINK_TYPES.map((t) => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="A end"><NodePicker value={f.a} onChange={(v) => set("a", v)} placeholder="Choose a node" /></Field>
          <Field label="Z end"><NodePicker value={f.z} onChange={(v) => set("z", v)} placeholder="Choose a node" /></Field>
          <Field label="Provider"><Input value={f.provider} onChange={(e) => set("provider", e.target.value)} /></Field>
          <Field label="Circuit ID"><Input value={f.circuit} onChange={(e) => set("circuit", e.target.value)} /></Field>
          <Field label="Capacity (Mbps)"><Input value={f.capacity} onChange={(e) => set("capacity", e.target.value.replace(/\D/g, ""))} inputMode="numeric" /></Field>
          <Field label="Expected RTT (ms)" hint="Baseline: new monitors warn at ×1.2 and go critical at ×1.45.">
            <Input value={f.rtt} onChange={(e) => set("rtt", e.target.value)} inputMode="decimal" placeholder="160" />
          </Field>
          <Field label="Depends on" hint="If that link is down, alerts on this one are suppressed as a consequence." className="sm:col-span-2">
            <Combobox options={dependsOptions} value={f.depends} onChange={(v) => set("depends", v)} empty="No link matches." />
          </Field>
          <Field label="External reference"><Input value={f.ref} onChange={(e) => set("ref", e.target.value)} /></Field>
          <TenantSelect value={f.tenant} onChange={(v) => set("tenant", v)} hint="The customer who buys this link sees it, its health and its SLA." />
        </div>
        <ErrorNote error={m.error} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!f.name.trim() || !f.a || !f.z || m.isPending}>{m.isPending && <Loader className="size-3.5 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
